import React, { useEffect, useMemo, useRef } from "react";
import { Skeleton } from "antd";
import { useQuery } from "@tanstack/react-query";
import * as echarts from "echarts";
import "echarts-wordcloud";

import { CURRENT_INDEX, COUNTER_DATA } from "./request/queryKeys";
import { fetchFrequencyByIndex, fetchFrequencyCounter } from "../../api/wordsApi";
import type { FrequencyResponse } from "../../api/common/types";

type CloudWord = { name: string; value: number };

const WordsVisualisation: React.FC = () => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  const { data: currentIndex = 1 } = useQuery<number>({
    queryKey: [CURRENT_INDEX],
    queryFn: async () => 1,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const { data: counter = 0 } = useQuery<number>({
    queryKey: [COUNTER_DATA],
    queryFn: fetchFrequencyCounter,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const { data, isLoading, isError } = useQuery<FrequencyResponse>({
    queryKey: ["FREQUENCY_RUN", currentIndex],
    queryFn: () => fetchFrequencyByIndex(currentIndex),
    enabled: counter > 0 && currentIndex > 0,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const words: CloudWord[] = useMemo(() => {
    const items = data?.items ?? [];
    return items.slice(0, 300).map((x) => ({ name: x.word, value: x.count }));
  }, [data]);

  useEffect(() => {
    if (!chartRef.current) return;

    const instance = echarts.init(chartRef.current);

    instance.setOption({
      backgroundColor: "transparent",
      tooltip: {
        show: true,
        formatter: (p: any) => `${p.name}: ${p.value}`,
      },
      series: [
        {
          type: "wordCloud",
          shape: "circle",
          left: "center",
          top: "center",
          width: "100%",
          height: "100%",
          sizeRange: [12, 80],
          rotationRange: [0, 90],
          rotationStep: 90,
          gridSize: 6,
          drawOutOfBound: false,
          textStyle: {
            color: () =>
              `rgb(${60 + Math.round(Math.random() * 160)}, ${60 + Math.round(
                Math.random() * 160
              )}, ${60 + Math.round(Math.random() * 160)})`,
          },
          emphasis: {
            focus: "self",
            textStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0,0,0,0.35)",
            },
          },
          data: words,
        },
      ],
    });

    const onResize = () => instance.resize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      instance.dispose();
    };
  }, [words]);

  if (counter === 0) {
    return (
      <div className="h-full min-h-0 flex items-center justify-center">
        <p className="text-white/70">No runs yet. Click “Add New Frequency”.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full min-h-0 flex flex-col">
        <div className="flex items-center justify-between gap-3">
          <span className="text-white/60 text-xs">Loading…</span>
        </div>

        <div className="mt-3 flex-1 min-h-0 rounded-2xl bg-white/5 border border-white/10 p-4 overflow-hidden">
          <Skeleton active title={false} paragraph={{ rows: 10, width: "80%" }} />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="h-full min-h-0 flex items-center justify-center">
        <p className="text-red-300">Failed to load data</p>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="flex items-center justify-end">
        <span className="text-white/60 text-xs">
          {data.items.length.toLocaleString()} items (top {words.length})
        </span>
      </div>

      <div className="mt-3 flex-1 min-h-0 rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        <div ref={chartRef} className="w-full h-[60vh] md:h-full" />
      </div>
    </div>
  );
};

export default WordsVisualisation;